import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Building2, ShieldCheck, Wrench, CalendarCheck, ClipboardCheck, CheckCircle2, Send, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const LOGO_URL = 'https://static.prod-images.emergentagent.com/jobs/b0feedf7-36df-41e9-9474-99c5d4f77205/images/70da477a4272b0edc6ede2f5e422bf85f189422131684b511c96d6b04c9062b9.jpeg';

const SERVICES = [
  {
    icon: ClipboardCheck,
    title: 'Ispezione e Sopralluogo',
    desc: 'Analizziamo lo stato di ogni serramento della tua struttura per individuare criticità e priorità di intervento.',
  },
  {
    icon: Wrench,
    title: 'Piano di Manutenzione',
    desc: 'Creiamo un programma su misura: regolazione, lubrificazione, sostituzione guarnizioni e piccole riparazioni.',
  },
  {
    icon: CalendarCheck,
    title: 'Interventi Programmati',
    desc: 'Pianifichiamo visite periodiche (1 o 2 volte l\'anno) per garantire il funzionamento ottimale tutto l\'anno.',
  },
  {
    icon: ShieldCheck,
    title: 'Assistenza Prioritaria',
    desc: 'In caso di emergenza, i clienti con piano di manutenzione hanno priorità assoluta negli interventi.',
  },
];

const BENEFITS = [
  'Riduzione dei costi di riparazione a lungo termine',
  'Miglioramento dell\'isolamento termico e acustico',
  'Maggiore sicurezza per ospiti e dipendenti',
  'Conformità alle normative di sicurezza',
  'Estetica sempre curata della struttura',
  'Un unico referente per tutti i serramenti',
];

export default function AziendePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    nome_azienda: '', referente: '', telefono: '', email: '',
    indirizzo: '', tipo_attivita: '', num_serramenti_approx: '', note: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome_azienda || !form.referente || !form.telefono) {
      toast.error('Compila almeno nome azienda, referente e telefono');
      return;
    }
    setSending(true);
    try {
      await api.post('/contact/business', form);
      setSent(true);
      toast.success('Richiesta inviata! Ti contatteremo presto.');
    } catch {
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setSending(false);
    }
  };

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="aziende-page">

      {/* NAV */}
      <nav className="sticky top-0 z-50 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="rounded-full" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <img src={LOGO_URL} alt="REGOLA" className="h-14" />
          </div>
          <a href="#contatto">
            <Button className="bg-blue-500 hover:bg-blue-600 text-white rounded-full px-6 text-sm font-semibold gap-2">
              Richiedi Sopralluogo
            </Button>
          </a>
        </div>
      </nav>

      {/* HERO */}
      <section className="py-16 md:py-24 bg-slate-900">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-8">
            <Building2 className="w-4 h-4 text-blue-400" />
            <span className="text-blue-400 text-xs font-semibold uppercase tracking-wide">Per aziende e strutture ricettive</span>
          </div>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-[1.15] tracking-tight mb-6"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            La manutenzione dei tuoi serramenti,{' '}
            <span className="text-blue-400">garantita tutto l'anno.</span>
          </h1>
          <p className="text-slate-400 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10">
            Hotel, B&B, uffici, negozi, condomini — ogni struttura ha decine di porte e finestre
            che necessitano di cura costante. Noi ce ne occupiamo per te.
          </p>
          <a href="#contatto">
            <Button size="lg" className="bg-blue-500 hover:bg-blue-600 text-white rounded-full px-10 py-6 text-base font-semibold gap-2">
              Richiedi un Sopralluogo Gratuito
            </Button>
          </a>
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-blue-500 text-xs font-bold uppercase tracking-[0.2em] mb-3">Come funziona</p>
            <h2
              className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Un percorso semplice,<br className="hidden md:block" /> risultati concreti
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((s, i) => (
              <Card key={i} className="border border-slate-100 rounded-xl hover:shadow-md transition-all hover:-translate-y-0.5">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center mb-5">
                    <s.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>{s.title}</h3>
                  </div>
                  <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* VANTAGGI */}
      <section className="py-20 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-blue-500 text-xs font-bold uppercase tracking-[0.2em] mb-3">Perche scegliere REGOLA</p>
              <h2
                className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-8"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Vantaggi di un piano<br /> di manutenzione
              </h2>
              <ul className="space-y-4">
                {BENEFITS.map((b, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-600 font-medium">
                    <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-slate-900 rounded-2xl p-8 md:p-10">
              <p className="text-blue-400 text-xs font-bold uppercase tracking-wider mb-4">Il nostro approccio</p>
              <p className="text-white text-lg font-bold leading-relaxed mb-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
                "Veniamo di persona, vediamo la struttura, e creiamo un piano che si adatta alle tue esigenze reali."
              </p>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Non vendiamo pacchetti preconfezionati. Ogni struttura e' diversa: un hotel sul lago ha esigenze
                diverse da un ufficio in citta'. Per questo il primo passo e' sempre un sopralluogo gratuito.
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">40+ anni di esperienza</p>
                  <p className="text-slate-400 text-xs">REGOLA by Rivas, dal 1984</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FORM CONTATTO */}
      <section id="contatto" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-blue-500 text-xs font-bold uppercase tracking-[0.2em] mb-3">Contattaci</p>
            <h2
              className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-3"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Richiedi un sopralluogo gratuito
            </h2>
            <p className="text-slate-500 text-sm max-w-md mx-auto">
              Compila il form e ti contatteremo per fissare un appuntamento di persona.
            </p>
          </div>

          {sent ? (
            <Card className="border-0 shadow-sm rounded-xl bg-emerald-50">
              <CardContent className="p-10 text-center">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>Richiesta inviata!</h3>
                <p className="text-sm text-slate-600 mb-6">Ti contatteremo il prima possibile per fissare il sopralluogo.</p>
                <Button onClick={() => navigate('/')} className="bg-blue-500 hover:bg-blue-600 text-white rounded-full px-6">
                  Torna alla Home
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-0 shadow-lg rounded-xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Nome Azienda / Struttura *</label>
                      <Input
                        placeholder="Es. Hotel Bellavista"
                        value={form.nome_azienda}
                        onChange={(e) => update('nome_azienda', e.target.value)}
                        className="rounded-lg"
                        data-testid="input-nome-azienda"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Tipo di Attivita'</label>
                      <Input
                        placeholder="Hotel, B&B, Ufficio, Negozio..."
                        value={form.tipo_attivita}
                        onChange={(e) => update('tipo_attivita', e.target.value)}
                        className="rounded-lg"
                        data-testid="input-tipo"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Referente *</label>
                      <Input
                        placeholder="Nome e Cognome"
                        value={form.referente}
                        onChange={(e) => update('referente', e.target.value)}
                        className="rounded-lg"
                        data-testid="input-referente"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Telefono *</label>
                      <Input
                        placeholder="+39 ..."
                        value={form.telefono}
                        onChange={(e) => update('telefono', e.target.value)}
                        className="rounded-lg"
                        data-testid="input-telefono"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Email</label>
                      <Input
                        placeholder="email@azienda.it"
                        value={form.email}
                        onChange={(e) => update('email', e.target.value)}
                        className="rounded-lg"
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Numero Serramenti (approssimativo)</label>
                      <Input
                        placeholder="Es. 50"
                        value={form.num_serramenti_approx}
                        onChange={(e) => update('num_serramenti_approx', e.target.value)}
                        className="rounded-lg"
                        data-testid="input-num-serramenti"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Indirizzo della struttura</label>
                    <Input
                      placeholder="Via, Citta', Provincia"
                      value={form.indirizzo}
                      onChange={(e) => update('indirizzo', e.target.value)}
                      className="rounded-lg"
                      data-testid="input-indirizzo"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Note o richieste particolari</label>
                    <Textarea
                      placeholder="Descrivi le tue esigenze..."
                      value={form.note}
                      onChange={(e) => update('note', e.target.value)}
                      className="rounded-lg min-h-[100px]"
                      data-testid="input-note"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={sending}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white rounded-full py-6 text-base font-semibold gap-2"
                    data-testid="submit-btn"
                  >
                    {sending ? 'Invio in corso...' : 'Invia Richiesta Sopralluogo'}
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-50 border-t border-slate-100 py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <img src={LOGO_URL} alt="REGOLA" className="h-10" />
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <div className="flex items-center gap-1.5">
              <Phone className="w-4 h-4 text-blue-500" />
              info@assistenzaregola.it
            </div>
            <div className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-blue-500" />
              Via Donatori di Sangue 2/d, 46043 Castiglione delle Stiviere (MN)
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
